<template>
  <div class="container py-4" style="min-height: calc(100vh - 120px);">
    <Loader v-if="loader" />

    <div class="row justify-content-center">
      <div class="col-12 col-lg-10">

        <div v-if="isCleared" class="card p-4 text-center">
          <div class="mb-3">
            <i class="fas fa-check-circle text-success" style="font-size: 48px;"></i>
          </div>
          <h4 class="mb-2">Payment received</h4>
          <p class="text-muted mb-4">
            Your subscription is up to date. If a recent payment isn't reflected yet, refresh in a moment.
          </p>
          <div>
            <button class="btn btn-primary me-2" @click="goToCompany">Enter company</button>
            <button class="btn btn-outline-secondary me-2" @click="fetchData">Refresh</button>
            <button class="btn btn-link" @click="goHome">Back to my companies</button>
          </div>
        </div>

        <div v-if="!isCleared" class="card">
          <div style="background: #DBE7F2" class="p-3">
            <h2 class="mb-1 text-danger">
              <i class="fas fa-exclamation-triangle me-1"></i>
              Subscription is expired
            </h2>
            <h4 v-if="isAdminUser" class="mb-0 text-dark">
              Please pay for renewal. Access to <strong>{{ companyName }}</strong> will resume once the overdue bills below are paid.
            </h4>
            <h4 v-if="!isAdminUser" class="mb-0 text-dark">
              Please contact your admin for renewal. Access to <strong>{{ companyName }}</strong> is paused until the subscription is renewed.
            </h4>
          </div>

          <div v-if="isAdminUser" class="p-3">
            <ListTable 
              :tableItems="tableItems" 
              v-model:selectedSubscriptions="selectedSubscriptions" 
              :showCheckbox="true"
              :showActions="false"
            />

            <div class="d-flex justify-content-between align-items-center mt-3">
              <button class="btn btn-link ps-0" @click="goHome">
                <i class="fas fa-arrow-left me-1"></i> Back to my companies
              </button>
              <div>
                <button class="btn btn-outline-secondary me-2" @click="fetchData">Refresh</button>
                <button class="btn btn-success" @click="payOnline" :disabled="!selectedSubscriptions.length">
                  Pay Online
                </button>
              </div>
            </div>
          </div>

          <div v-if="!isAdminUser" class="p-3">
            <div class="d-flex justify-content-between align-items-center">
              <button class="btn btn-link ps-0" @click="goHome">
                <i class="fas fa-arrow-left me-1"></i> Back to my companies
              </button>
              <button class="btn btn-outline-secondary" @click="fetchData">Refresh</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/loader/Loader.vue';
import ListTable from '@/components/molecule/subscription/ListTable.vue';

import handleOnlinePayment from '@/services/modules/onlinePayment.js';
import handleCompany from '@/services/modules/company';
import handleSidebar from '@/services/modules/sidebar';
import handleRole from '@/services/modules/role';
import moneyReceiptHelper from "@/services/utils/pdf/moneyReceiptHelper";

const router = useRouter();
const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchSubscriptions, initiateSubscriptionPayment, fetchSubscriptionReceipt } = handleOnlinePayment();
const { fetchCompanyInfo } = handleCompany();
const { fetchCompanyModuleByFirstMenu } = handleSidebar();
const { checkUserHasRoles } = handleRole();
const { generatePdf } = moneyReceiptHelper();

const companyId = computed(() => route.params.companyId);

const tableItems = ref([]);
const selectedSubscriptions = ref([]);
const companyName = ref('');
const conpanyInfo = ref({});
const loader = ref(false);
const isCleared = ref(false);
const isAdminUser = ref(false);

const fetchAdminRole = async () => {
    try {
        const query = `?company_id=${companyId.value}&roles[]=software_admin`;
        const res = await checkUserHasRoles(query);
        if (!res.status) return;
        isAdminUser.value = res.data.has_role;
    } catch (error) {
        console.log(error)
    }
};

const fetchCompany = async () => {
    try {
        const res = await fetchCompanyInfo(companyId.value);
        if (!res.status) return;
        conpanyInfo.value = res.data;
        companyName.value = res.data.name;
    } catch (error) {
        showError('Something went wrong');
    }
};

const fetchData = async () => {
    try {
        loader.value = true;
        selectedSubscriptions.value = [];

        const query = `?company_id=${companyId.value}&status=due&is_all=true&is_expired=true`;
        const res = await fetchSubscriptions(query);

        if (!res.status) {
            tableItems.value = [];
            return;
        }

        const rows = Array.isArray(res.data) ? res.data : (res.data.data || []);
        tableItems.value = rows;
        isCleared.value = rows.length === 0;
    } catch (error) {
        showError('Failed to fetch subscriptions.');
        tableItems.value = [];
    } finally {
        loader.value = false;
    }
};

const payOnline = async () => {
    if (selectedSubscriptions.value.length === 0) {
        showError('Please select a subscription to pay');
        return;
    }

    const successRoute = router.resolve({
        name: 'SubscriptionDue',
        params: { companyId: companyId.value },
    });
    const successUrl = window.location.origin + successRoute.href;

    try {
        const res = await initiateSubscriptionPayment({
            subscription_ids: selectedSubscriptions.value,
            company_id: companyId.value,
            return_url: successUrl,
            success_url: successUrl,
        });

        if (!res.status || !res.gateway_url) {
            showError('Failed to initiate payment.');
            return;
        }

        window.location.href = res.gateway_url;
    } catch (error) {
        showError('Something went wrong during payment initiation.');
    }
};

const goToCompany = () => {
    fetchCompanyModuleByFirstMenu(companyId.value);
};

const goHome = () => {
    router.push({ name: 'Home' });
};

const handleReceiptGeneration = async (receiptId) => {
    try {
        const companyQuery = `?company_id=${companyId.value}`;
        const receiptRes = await fetchSubscriptionReceipt(receiptId, companyQuery);
        if (receiptRes && receiptRes.status) {
            generatePdf(conpanyInfo.value, receiptRes.data);
        }
    } catch (err) {
        showError("An error occurred while generating the receipt.");
    }
};

onMounted(async () => {
    const { payment_status, receipt_id, message } = route.query;

    if (payment_status === 'success') {
        showSuccess('Payment processed successfully!');
        router.replace({ query: {} });
    }
    
    await fetchCompany();
    fetchAdminRole();
    fetchData();

    if (payment_status === 'success' && receipt_id) {
        await handleReceiptGeneration(receipt_id);
    }
    
    if (payment_status === 'failed' || payment_status === 'error' || payment_status === 'cancel') {
        showError('Payment failed');
        router.replace({ query: {} });
    }
});
</script>
